import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { SideDrawer } from '@/modules/common/SideDrawer';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useDocumentDownload } from '@/modules/files/hooks/useFileDownload';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useOpenValidationQuestions } from '@/modules/validationQuestions/hooks/useOpenValidationQuestions';
import { useCurrentUserWorkflowTrackersContext } from '@/modules/workflowTrackers/contexts/useCurrentUserWorkflowTrackersContext';
import { mapWorkflowTrackerToActivityItem } from '@/modules/workflowTrackers/utils/mapWorkflowTrackerToActivityItem';
import { THEME_SHADOWS } from '@/styles/themes/common';
import { diagnostics } from '@/utils/diagnostics';

import { InboxButton } from '../components/InboxButton/InboxButton';
import { NotificationsTray } from '../components/NotificationsTray/NotificationsTray';
import { ProcessingIndicator } from '../components/ProcessingIndicator/ProcessingIndicator';
import { ValidationNotificationCallout } from '../components/ValidationNotificationCallout/ValidationNotificationCallout';
import { ValidationQuestionsHouseholdModal } from '../components/ValidationQuestionsHouseholdModal/ValidationQuestionsHouseholdModal';
import { AppNotification } from '../NotificationContext/context/NotificationsContext.context';
import { useNotificationContext } from '../NotificationContext/hooks/useNotificationContext';
import { ReviewAISuggestionsFromNotification } from '../ReviewAISuggestionsFromNotification/ReviewAISuggestionsFromNotification';
import {
  getCurrentHouseholdValidationNotification,
  getValidationNotificationTarget,
  useValidationNotifications,
} from './hooks/useValidationNotification';
import { mapNotificationToTrayData } from './NotificationContainer.utils';
import { shouldShowProcessingIndicator } from './processingIndicator.utils';

export interface NotificationContainerProps {
  /**
   * Variant of the inbox button
   * @default 'dark'
   */
  variant?: 'dark' | 'light';
}

export function NotificationContainer({
  variant = 'dark',
}: NotificationContainerProps) {
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'Notifications' | 'Activities'>(
    'Notifications'
  );
  const [isAISuggestionsModalOpen, setIsAISuggestionsModalOpen] =
    useState(false);
  const [isValidationHouseholdModalOpen, setIsValidationHouseholdModalOpen] =
    useState(false);
  const [aiSuggestionsHouseholdId, setAiSuggestionsHouseholdId] = useState<
    string | null
  >(null);
  const navigate = useNavigate();
  const { showFeedback } = useFeedback();
  const trackUserEvent = useTrackUserEvent();
  const [downloadDocument] = useDocumentDownload();

  const {
    notifications,
    unreadCount,
    markRead,
    markUnread,
    dismiss,
    dismissAll: dismissAllNotifications,
    markAllRead,
    hasNewNotifications,
    updateLastViewed,
    loadOlder,
    hasMore,
    loadingMore,
    unreadValidationQuestionNotifications,
  } = useNotificationContext();

  // Null outside a household, such as on the client list.
  const { householdId: currentHouseholdId } = useHouseholdDetailsContext();
  const validationNotifications = useValidationNotifications();
  const currentHouseholdValidationNotification =
    getCurrentHouseholdValidationNotification(
      validationNotifications,
      currentHouseholdId
    );

  useEffect(() => {
    if (validationNotifications.length === 0) {
      setIsValidationHouseholdModalOpen(false);
    }
  }, [validationNotifications.length]);

  const openHouseholdValidationQuestions = useOpenValidationQuestions();

  const workflowTrackersContext = useCurrentUserWorkflowTrackersContext();
  const {
    trackers,
    hasMore: hasMoreTrackers,
    loadingMore: loadingMoreTrackers,
    dismissAll: dismissAllTrackers,
    loadOlder: loadOlderTrackers,
  } = workflowTrackersContext;

  const handleLoadOlderTrackers = () => {
    void loadOlderTrackers();
  };

  const handleDismissAllTrackers = () => {
    void dismissAllTrackers();
  };

  const handleInboxClick = () => {
    setActiveTab('Notifications');
    setIsOpen(true);
    // Update last viewed timestamp when opening tray
    void updateLastViewed();
  };

  const handleProcessingIndicatorClick = () => {
    setActiveTab('Activities');
    setIsOpen(true);
    // Update last viewed timestamp when opening tray
    void updateLastViewed();
  };

  const handleClose = () => {
    setIsOpen(false);
    // Update last viewed timestamp when closing tray
    void updateLastViewed();
  };

  const handleClearAll = () => {
    void dismissAllNotifications();
  };

  const handleMarkAllAsRead = () => {
    void markAllRead();
  };

  const handleOpenAISuggestionsModal = (householdId: string) => {
    setAiSuggestionsHouseholdId(householdId);
    setIsAISuggestionsModalOpen(true);
    handleClose(); // Close the notifications tray
  };

  const openValidationQuestions = (notification: AppNotification) => {
    const target = getValidationNotificationTarget(notification);
    if (!target) {
      showFeedback(
        'This notification is missing some data and cannot be opened. Our team has been notified.'
      );
      diagnostics.error(
        'Validation notification has no household or conversation target',
        undefined,
        { notificationId: notification.id }
      );
      return;
    }

    unreadValidationQuestionNotifications
      .filter((candidate) => candidate.household?.id === target.householdId)
      .forEach((candidate) => {
        void markRead(candidate.id);
      });
    trackUserEvent('notification clicked', {
      notificationId: notification.id,
      notificationKind: notification.kind,
    });
    openHouseholdValidationQuestions(target.householdId, target.conversationId);
  };

  const handleValidationCalloutClick = () => {
    if (
      validationNotifications.length === 1 &&
      currentHouseholdValidationNotification
    ) {
      openValidationQuestions(currentHouseholdValidationNotification);
      return;
    }

    setIsValidationHouseholdModalOpen(true);
  };

  const handleValidationHouseholdSelect = (notification: AppNotification) => {
    setIsValidationHouseholdModalOpen(false);
    openValidationQuestions(notification);
  };

  const handleCloseAISuggestionsModal = () => {
    setIsAISuggestionsModalOpen(false);
    setAiSuggestionsHouseholdId(null);
  };

  // Transform notifications to match NotificationsTray interface
  const trayNotifications = notifications.map((notification) =>
    mapNotificationToTrayData(notification, {
      closeDrawer: handleClose,
      markRead,
      markUnread,
      dismiss,
      trackUserEvent,
      navigateTo: (path) => navigate(path),
      showFeedback,
      downloadDocument,
      openAISuggestionsModal: handleOpenAISuggestionsModal,
      openValidationQuestions: openHouseholdValidationQuestions,
    })
  );

  // Transform workflow trackers to activity items
  const trayActivities = trackers.map(mapWorkflowTrackerToActivityItem);

  // Don't consider activities for the unread count; only notifications
  const totalUnreadCount = unreadCount;
  const hasNewItems = hasNewNotifications;
  const showProcessingIndicator = shouldShowProcessingIndicator(
    trackers,
    validationNotifications[0]?.household?.id
  );

  return (
    <>
      <Stack spacing={1} alignItems="center" direction="row">
        <InboxButton
          variant={variant}
          notificationCount={totalUnreadCount}
          onClick={handleInboxClick}
          showNewText={hasNewItems && !isOpen}
        />
        {!enhanced && validationNotifications.length > 0 && (
          <ValidationNotificationCallout
            householdCount={validationNotifications.length}
            includesCurrentHousehold={Boolean(
              currentHouseholdValidationNotification
            )}
            onClick={handleValidationCalloutClick}
          />
        )}
        {showProcessingIndicator && (
          <ProcessingIndicator
            variant={variant}
            onClick={handleProcessingIndicatorClick}
          />
        )}
      </Stack>

      <SideDrawer
        isOpen={isOpen}
        anchor="left"
        onClose={handleClose}
        width={450}
        drawerPropsSx={{
          height: '100vh',
          boxShadow: THEME_SHADOWS.lg,
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        <NotificationsTray
          notifications={trayNotifications}
          activities={trayActivities}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onClose={handleClose}
          onClearAll={handleClearAll}
          onMarkAllAsRead={handleMarkAllAsRead}
          loadOlder={loadOlder}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onClearCompletedActivities={handleDismissAllTrackers}
          onLoadOlderActivities={handleLoadOlderTrackers}
          hasMoreActivities={hasMoreTrackers}
          loadingMoreActivities={loadingMoreTrackers}
        />
      </SideDrawer>

      <ReviewAISuggestionsFromNotification
        isOpen={isAISuggestionsModalOpen}
        onClose={handleCloseAISuggestionsModal}
        householdId={aiSuggestionsHouseholdId}
      />

      {!enhanced && isValidationHouseholdModalOpen && (
        <ValidationQuestionsHouseholdModal
          notifications={validationNotifications}
          onClose={() => setIsValidationHouseholdModalOpen(false)}
          onSelect={handleValidationHouseholdSelect}
        />
      )}
    </>
  );
}
